import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal } from 'antd';

import { queryClient } from '@/Root';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { PageProps } from '@/types/PageProps';
import {
  Insurer,
  ListInsurersQuery,
} from '@/core/domain/people/Insurers/Insurers.types';
import { useDeleteInsurer } from '@/core/domain/people/Insurers/Insurers.hooks';
import InsurersList from '@/components/Insurers/InsurersList';
import InsurersFilters from '@/components/Insurers/InsurersFilters';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InsurerReportModal from '@/components/Insurers/InsurerReportModal';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const InsurersPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [insurerModal, insurerModalAction] = useDisclosure(false);

  const [params, setParams] = useState<ListInsurersQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListInsurersQuery>({
      name: 'string',
      cnpj: 'string',
      fantasyName: 'string',
      order: 'string',
      active: 'string',
    }),
  });

  const queryDebounce = useDebounce(params, 500);

  usePersistSearchParams(queryDebounce);

  const removeMutation = useDeleteInsurer();

  const handleRemoveClick = (insurer: Insurer) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'insurers.remove.modal.title' }),
      content: intl.formatMessage({
        id: 'insurers.remove.modal.content',
      }),
      onOk: async () => {
        try {
          await removeMutation.mutateAsync(insurer.id);

          await queryClient.refetchQueries('insurers');
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'insurers.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  return (
    <>
      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Button type="primary" onClick={insurerModalAction.open}>
              Gerar relatório
            </Button>
            <Button
              block
              type="primary"
              onClick={() => history.push('/app/registry/insurers/create')}
              disabled={!hasPermissions([Authorities.REGISTER_INSURER_ADD])}
            >
              <FormattedMessage id="insurers.searchBar.button" />
            </Button>
          </SpaceForButton>
        }
      >
        <Helmet title={intl.formatMessage({ id: route.name })} />

        <InsurersFilters queryParams={params} setQueryParams={setParams} />

        <InsurersList
          params={queryDebounce}
          handleRemoveClick={handleRemoveClick}
        />

        <InsurerReportModal
          open={insurerModal}
          onCancel={insurerModalAction.close}
        />
      </TabContentWrapper>
    </>
  );
};

export default InsurersPage;
